import React from "react";
import { useTheme } from "contexts/ThemeContext";

interface LoaderProps {
  size?: "sm" | "md" | "lg" | "xl";
  color?: "primary" | "success" | "warning" | "info";
  fullScreen?: boolean;
}

const sizeMap = {
  sm: "20px",
  md: "24px",
  lg: "32px",
  xl: "40px",
};

const borderWidthMap = {
  sm: "2px",
  md: "3px",
  lg: "4px",
  xl: "5px",
};

const Loader: React.FC<LoaderProps> = ({
  size = "md",
  color = "primary",
  fullScreen = false,
}) => {
  const { getThemeColor } = useTheme();

  const colorMap: Record<string, string> = {
    primary: getThemeColor("--app-primary", "#7c3aed"),
    success: getThemeColor("--status-success", "#22c55e"),
    warning: getThemeColor("--status-warning", "#f59e0b"),
    info: getThemeColor("--app-info", "#3b82f6"),
  };

  const spinnerStyle: React.CSSProperties = {
    width: sizeMap[size],
    height: sizeMap[size],
    borderWidth: borderWidthMap[size],
    borderStyle: "solid",
    borderColor: `${colorMap[color]}20`,
    borderTopColor: colorMap[color],
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
  };

  const spinner = <div style={spinnerStyle} />;

  if (fullScreen) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white/80 backdrop-blur-sm z-50">
        {spinner}
      </div>
    );
  }

  return spinner;
};

// Inject keyframes for spin animation
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;
if (!document.head.querySelector('style[data-loader-animation]')) {
  styleSheet.setAttribute('data-loader-animation', 'true');
  document.head.appendChild(styleSheet);
}

export default Loader;
